import { injectable, inject } from 'inversify';
import { Result, ok, err } from '@xlearn/common'
import * as Api from '../api'

@injectable()
export class DefaultTypedStateSerializer implements Api.TypedStateSerializer {

  constructor(
    @inject(Api.StateSerializationFactorySymbol)
    private serializerFactory: Api.StateSerializationFactory) {
  }
  
  serialize<T>(
    name: Api.StateSerializerName, format: string,
    state: T): Result<Api.SerializeResult, Api.TypedStateSerializeError> {

    const findResult = this.findSerializer(name, format)
    if (!findResult.success) {
      return findResult
    }

    const serializer = findResult.value
    const serializeResult = serializer.serialize(state)
    if (!serializeResult.success) {
      return err({ code: 'serialize-error' as 'serialize-error', details: serializeResult.error })
    }

    return ok({
      buffer: serializeResult.value,
      serializerId: findResult.value.key.id
    })
  }

  deserialize<T>(nameOrId: Api.StateSerializerName | number,
                 format: string,
                 buffer: Uint8Array): Result<Api.DeserializeResult<T>, Api.TypedStateDeserializeError> {

    const findResult = this.findSerializer<T>(nameOrId, format)
    if (!findResult.success) {
      return err(findResult.error)
    }

    const serializer = findResult.value
    const deserializeResult = serializer.deserialize(buffer)
    if (!deserializeResult.success) {
      return err({ code: 'deserialize-error' as 'deserialize-error',
                   details: deserializeResult.error })
    }

    return ok({
      content: deserializeResult.value,
      serializerName: findResult.value.key.name
    })
  }

  private findSerializer<T>(nameOrId: Api.StateSerializerName | number,
                            format: string): Result<Api.StateSerializationPlugin<T>, Api.SerializerNotFoundError> {

    let findSerializerResult: Result<Api.StateSerializationPlugin<T>, 'not-found'>
    if (typeof nameOrId === 'number') {
      findSerializerResult = this.serializerFactory.resolveById<T>(nameOrId, format)
    } else {
      findSerializerResult = this.serializerFactory.resolveByName<T>(nameOrId, format)
    }
    if (!findSerializerResult.success) {
      return err({ code: 'serializer-not-found' as 'serializer-not-found' , details: nameOrId})
    }

    return ok(findSerializerResult.value)
  }
}
